import { useState, useEffect, useCallback } from 'react'
import { Search, Calendar, Clock, CheckCircle, XCircle, Eye, Loader2, X, ChevronLeft, ChevronRight } from 'lucide-react'
import { pointsApi } from '../../lib/api'

const PAGE_SIZE = 20

const MedicalEvents = () => {
  const [records, setRecords] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const [selectedRecord, setSelectedRecord] = useState<any | null>(null)
  const [cancellingRecord, setCancellingRecord] = useState<any | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [actionLoading, setActionLoading] = useState(false)

  const loadRecords = useCallback(async () => {
    setLoading(true)
    try {
      const params: any = { page: currentPage, page_size: PAGE_SIZE }
      if (statusFilter !== 'all') params.status = statusFilter
      if (searchTerm.trim()) params.keyword = searchTerm.trim()
      
      const resp = await pointsApi.records(params) as any
      setTotal(resp.total || 0)
      setTotalPages(resp.total_pages || 1)
      
      const items = resp.records || []
      setRecords(items.map((r: any) => ({
        id: r.id,
        userId: r.user_id,
        userName: r.user_name || '未知用户',
        userPhone: r.user_phone || '',
        eventType: r.event_type_name || '未知',
        eventTypeId: r.event_type_id,
        points: r.points,
        status: r.status,
        hospital: r.hospital_name || '',
        createdAt: r.created_at ? new Date(r.created_at).toLocaleString('zh-CN') : '',
        confirmedAt: r.confirmed_at ? new Date(r.confirmed_at).toLocaleString('zh-CN') : '',
        cancelReason: r.cancel_reason || '',
        note: r.note || '',
      })))
    } catch (e) {
      console.error('Failed to load records', e)
    } finally {
      setLoading(false)
    }
  }, [currentPage, statusFilter, searchTerm])

  useEffect(() => { loadRecords() }, [loadRecords])

  const handleSearch = () => {
    setCurrentPage(1)
    loadRecords()
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch()
    }
  }

  const handleCancel = async () => {
    if (!cancelReason.trim() || !cancellingRecord) return
    setActionLoading(true)
    try {
      await pointsApi.cancel(cancellingRecord.id, cancelReason)
      loadRecords()
      setCancellingRecord(null)
      setCancelReason('')
    } catch (e) {
      console.error('Cancel failed', e)
    } finally {
      setActionLoading(false)
    }
  }

  const getStatusConfig = (status: string) => {
    switch (status) {
      case 'confirmed': return { label: '已确认', icon: CheckCircle, bg: 'bg-green-100', textColor: 'text-green-700', color: 'text-green-600' }
      case 'pending': return { label: '待确认', icon: Clock, bg: 'bg-orange-100', textColor: 'text-orange-700', color: 'text-orange-600' }
      case 'cancelled': return { label: '已撤销', icon: XCircle, bg: 'bg-red-100', textColor: 'text-red-700', color: 'text-red-600' }
      default: return { label: status, icon: Clock, bg: 'bg-gray-100', textColor: 'text-gray-700', color: 'text-gray-600' }
    }
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-2xl shadow-md p-6">
        <h1 className="text-xl font-bold text-gray-800">事件列表</h1>
        <p className="text-gray-500 text-sm mt-1">查看和管理所有积分事件记录</p>
      </div>

      <div className="bg-white rounded-2xl shadow-md p-4">
        <div className="flex flex-col lg:flex-row gap-4">
          <div className="flex-1 relative flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input type="text" placeholder="搜索用户姓名、手机号、事件类型..."
                value={searchTerm} 
                onChange={(e) => setSearchTerm(e.target.value)}
                onKeyPress={handleKeyPress}
                className="w-full pl-12 pr-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" />
            </div>
            <button 
              onClick={handleSearch}
              className="px-6 py-3 bg-green-700 text-white rounded-xl hover:bg-green-800 transition-colors font-medium"
            >
              搜索
            </button>
          </div>
          <div className="flex gap-2 flex-wrap">
            {[{ key: 'all', label: '全部' }, { key: 'pending', label: '待确认' }, { key: 'confirmed', label: '已确认' }, { key: 'cancelled', label: '已撤销' }].map(f => (
              <button key={f.key} onClick={() => { setStatusFilter(f.key); setCurrentPage(1) }}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors ${statusFilter === f.key ? 'bg-green-700 text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'}`}>
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-md overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-green-600" /></div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">用户信息</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">事件类型</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">积分</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">机构</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">状态</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-gray-600">时间</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-gray-600">操作</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {records.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-400">暂无记录</td>
                  </tr>
                ) : (
                  records.map((record) => {
                    const statusConfig = getStatusConfig(record.status)
                    const StatusIcon = statusConfig.icon
                    return (
                      <tr key={record.id} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                              <span className="text-green-700 font-semibold">{record.userName.charAt(0)}</span>
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{record.userName}</p>
                              <p className="text-xs text-gray-500">{record.userPhone}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center">
                            <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                            <span className="text-gray-700">{record.eventType}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4"><span className="font-bold text-green-600">+{record.points}</span></td>
                        <td className="px-6 py-4 text-gray-600 text-sm">{record.hospital}</td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${statusConfig.bg} ${statusConfig.textColor}`}>
                            <StatusIcon className={`w-3 h-3 mr-1 ${statusConfig.color}`} />
                            {statusConfig.label}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-500">{record.createdAt}</td>
                        <td className="px-6 py-4 text-right">
                          <div className="flex items-center justify-end space-x-2">
                            <button onClick={() => setSelectedRecord(record)}
                              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors" title="查看详情">
                              <Eye className="w-4 h-4" />
                            </button>
                            {record.status === 'pending' && (
                              <button onClick={() => { setCancellingRecord(record); setCancelReason('') }}
                                className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="撤销">
                                <XCircle className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          )}
        </div>

        {/* 分页 */}
        {totalPages > 1 && (
          <div className="px-6 py-4 flex items-center justify-between border-t">
            <div className="text-sm text-gray-500">
              共 {total} 条记录，第 {currentPage} / {totalPages} 页
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))} 
                disabled={currentPage === 1}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-200 flex items-center gap-1"
              >
                <ChevronLeft className="w-4 h-4" />
                上一页
              </button>
              <button 
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} 
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg disabled:opacity-50 hover:bg-gray-200 flex items-center gap-1"
              >
                下一页
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* 详情弹窗 */}
      {selectedRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-md p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-800">积分记录详情</h3>
              <button onClick={() => setSelectedRecord(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5" /></button>
            </div>
            <div className="space-y-3 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">用户</span><span className="text-gray-800">{selectedRecord.userName}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">手机号</span><span className="text-gray-800">{selectedRecord.userPhone}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">事件类型</span><span className="text-gray-800">{selectedRecord.eventType}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">积分</span><span className="text-green-600 font-bold">+{selectedRecord.points}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">机构</span><span className="text-gray-800">{selectedRecord.hospital}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">状态</span>
                <span className={getStatusConfig(selectedRecord.status).textColor}>{getStatusConfig(selectedRecord.status).label}</span>
              </div>
              <div className="flex justify-between"><span className="text-gray-500">创建时间</span><span className="text-gray-800">{selectedRecord.createdAt}</span></div>
              {selectedRecord.note && <div className="flex justify-between"><span className="text-gray-500">备注</span><span className="text-gray-800">{selectedRecord.note}</span></div>}
              {selectedRecord.cancelReason && <div className="flex justify-between"><span className="text-gray-500">撤销原因</span><span className="text-red-600">{selectedRecord.cancelReason}</span></div>}
            </div>
          </div>
        </div>
      )}

      {/* 撤销弹窗 */}
      {cancellingRecord && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-sm p-6">
            <h3 className="text-lg font-bold text-gray-800 mb-2">撤销积分</h3>
            <p className="text-sm text-gray-500 mb-3">
              撤销给用户「{cancellingRecord.userName}」的 <span className="text-red-500 font-bold">-{cancellingRecord.points}</span> 积分
            </p>
            <textarea value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="请输入撤销原因（必填）"
              className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500 text-sm mb-3" rows={3} />
            <div className="flex gap-3">
              <button onClick={() => setCancellingRecord(null)} className="flex-1 py-3 border border-gray-200 rounded-xl hover:bg-gray-50">取消</button>
              <button onClick={handleCancel} disabled={actionLoading || !cancelReason.trim()}
                className={`flex-1 py-3 rounded-xl text-white font-medium ${actionLoading || !cancelReason.trim() ? 'bg-gray-300' : 'bg-red-600 hover:bg-red-700'}`}>
                {actionLoading ? '处理中...' : '确认撤销'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default MedicalEvents
